import React, { useState, useEffect } from 'react';
import { Todo, UpdateTodoDto } from '../types/todo';
import { FaCheck, FaTrash, FaUndo, FaEye, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import Modal from 'react-modal';

interface TodoItemProps {
  todo: Todo;
  onUpdate: (id: string, data: UpdateTodoDto) => void;
  onDelete: (id: string) => void;
}

const youtubeVideos = [
  'QGtoCUwQXOk', // Example video 1
  '6rvv8bU3pKA', // Example video 2
  'z63KyqrIpok', // Example video 3
  'k9EtfTAirBs',
  '_NAflV4UUdU'
];

const TodoItem: React.FC<TodoItemProps> = ({ todo, onUpdate, onDelete }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [originalTitle] = useState(document.title);
  const [isVideoVisible, setIsVideoVisible] = useState(false); // Video section visibility
  const [randomVideo, setRandomVideo] = useState<string | null>(null); // Random video ID

  const openModal = () => {
    setTimeLeft(25 * 60); // Reset the timer
    setIsModalOpen(true);
    const randomIndex = Math.floor(Math.random() * youtubeVideos.length);
    setRandomVideo(youtubeVideos[randomIndex]);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.title = originalTitle; // Restore the original title
  };

  // Timer logic
  useEffect(() => {
    if (!isModalOpen) {
      document.title = originalTitle; // Reset the tab title when modal is closed
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(timer);
          document.title = 'Time is up!';
          setIsModalOpen(false);
          return 0;
        }
        const updatedTime = prevTime - 1;
        document.title = `Focus: ${formatTime(updatedTime)}`;
        return updatedTime;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      document.title = originalTitle; // Restore the original title
    };
  }, [isModalOpen, originalTitle]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex justify-between items-center bg-gray-800 p-4 rounded shadow text-gray-200">
      <div>
        <h3 className={`text-lg font-medium ${todo.isCompleted ? 'line-through text-gray-500' : ''}`}>
          {todo.title}
        </h3>
        <p className="text-sm text-gray-400">{todo.description}</p>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onUpdate(todo.id, { ...todo, isCompleted: !todo.isCompleted })}
          className="text-green-400 hover:text-green-500 transition"
        >
          {todo.isCompleted ? <FaUndo size={20} /> : <FaCheck size={20} />}
        </button>
        <button
          onClick={openModal}
          className="text-blue-400 hover:text-blue-500 transition"
        >
          <FaEye size={20} />
        </button>
        <button
          onClick={() => onDelete(todo.id)}
          className="text-red-400 hover:text-red-500 transition"
        >
          <FaTrash size={20} />
        </button>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        overlayClassName="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center"
        className="bg-gray-800 text-gray-200 p-6 rounded max-w-md w-full text-center"
      >
        <h2 className="text-2xl font-bold mb-4">Now you're focused on
          <p>
            "{todo.title}"
          </p>
        </h2>
        <p className="text-4xl font-mono mb-4">{formatTime(timeLeft)}</p>
        <button
          onClick={() => setIsVideoVisible((prev) => !prev)}
          className="text-blue-400 hover:text-blue-500 mb-4 flex items-center justify-center mx-auto"
        >
          {isVideoVisible ? (
            <>
              <FaChevronUp size={16} className="mr-2" />
              Hide
            </>
          ) : (
            <>
              <FaChevronDown size={16} className="mr-2" />
              Mark well the rhythm of industrious purpose.
            </>
          )}
        </button>
        {isVideoVisible && randomVideo && (
          <div className="mt-4">
            <iframe
              width="100%"
              height="200"
              src={`https://www.youtube.com/embed/${randomVideo}`}
              title="Random YouTube Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded"
            ></iframe>
          </div>
        )}
        <button
          onClick={closeModal}
          className={`bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded ${isVideoVisible ? 'mt-4' : ''}`}
        >
          I give up
        </button>
      </Modal>
    </div>
  );
};

export default TodoItem;
